import { useQuery } from "@apollo/client/react";
import { useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import { getFiltersPredicationFromURI } from "components/Filters/helpers";
import { useCachedFilterFields } from "components/Filters/hooks";
import { SortOption } from "components/Filters/types";
import { getSearchQuery } from "components/SearchInput/helpers";
import { WorkerPool } from "types/generated";
import { FILTER_ITEMS_DICTIONARY } from "views/Account/WorkerPool/QueuedRunsView/constants";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import { SEARCH_PRIVATE_WORKER_POOL_SCHEDULABLE_RUNS_SUGGESTIONS } from "./gql";

type UseSearchPrivateWorkerPoolsSuggestions = {
  workerPoolId?: string;
};

const useSearchQueuedRunsSuggestions = ({
  workerPoolId,
}: UseSearchPrivateWorkerPoolsSuggestions) => {
  const fieldsRef = useRef<string[] | null>(null);
  const [searchParams] = useSearchParams();
  const searchInput = getSearchQuery(searchParams);

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(searchParams, true);

    return [...(predicatesMap?.values() || [])];
  }, [searchParams]);

  const { data, loading, refetch, previousData } = useQuery<{
    workerPool: WorkerPool;
  }>(SEARCH_PRIVATE_WORKER_POOL_SCHEDULABLE_RUNS_SUGGESTIONS, {
    variables: {
      workerPoolId,
      input: {
        predicates,
        fullTextSearch: searchInput,
        fields: fieldsRef.current,
      },
    },
    skip: !workerPoolId,
  });

  const rawSearchRunsSuggestions = useMemo(() => {
    if (data?.workerPool) {
      const { searchSchedulableRunsSuggestions } = data.workerPool;

      return searchSchedulableRunsSuggestions;
    }

    return undefined;
  }, [data]);

  const cachedFiltersData = useCachedFilterFields(rawSearchRunsSuggestions?.fields);

  const sortOptions = useMemo((): SortOption[] => {
    return cachedFiltersData
      .filter((field) => field.orderable)
      .map((field) => {
        return {
          value: field.name,
          label: field.name,
        };
      });
  }, [cachedFiltersData]);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: FILTER_ITEMS_DICTIONARY,
  });

  const handleRefetchActiveSections = (fields: string[]) => {
    fieldsRef.current = fields.length > 0 ? fields : null;

    if (fields.length > 0) {
      refetch({
        input: {
          fullTextSearch: searchInput,
          predicates,
          fields,
        },
      });
    }
  };

  const previousSearchRunsSuggestions = previousData?.workerPool?.searchSchedulableRunsSuggestions;

  return {
    filters,
    filtersMap: filterOptionsMap,
    sortOptions,
    loading,
    refetchActiveSections: handleRefetchActiveSections,
    filteredCount:
      rawSearchRunsSuggestions?.filteredCount || previousSearchRunsSuggestions?.filteredCount || 0,
  };
};

export default useSearchQueuedRunsSuggestions;
